// Internal analytics — sends events to JyotishPuraan backend
// Fire and forget — never awaited, never blocks UI

const BACKEND = 'https://app.jyotishpuraan.com';
const DEVICE_ID_KEY = 'jp_device_id';

function _uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  // Fallback for older browsers / non-secure contexts.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Stable per-browser id (survives logout) so anonymous visitors are countable
// and events can be attributed to a device even before login.
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = _uuid();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null; // private mode / storage blocked — send without device_id
  }
}

// Stable per-tab session id — tracks a single browsing session for funnel
// correlation. Distinct from device_id (which survives logout/tab-close).
export function getSessionId() {
  try {
    let id = sessionStorage.getItem('jp_session_id');
    if (!id) {
      id = _uuid();
      sessionStorage.setItem('jp_session_id', id);
    }
    return id;
  } catch {
    return null;
  }
}

// D12: Funnel instrumentation — sends shell_rendered / app_mounted / step
// events to POST /api/leads/funnel-event with session_id correlation.
export function trackFunnel(event, properties = {}) {
  try {
    const params = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search || '')
      : new URLSearchParams();
    fetch(`${BACKEND}/api/leads/funnel-event`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event,
        action: event,
        session_id: getSessionId(),
        device_id: getDeviceId(),
        url: typeof window !== 'undefined' ? window.location.pathname : '',
        referrer: typeof document !== 'undefined' ? (document.referrer || '') : '',
        intent: params.get('intent') || '',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
        ...properties,
      }),
    }).catch(() => {});
  } catch {}
}

export function trackEvent(event, properties = {}) {
  try {
    const token = localStorage.getItem('jyotish_session_token') || '';
    fetch(`${BACKEND}/api/analytics/track`, {
      method: 'POST',
      // keepalive lets the request survive a route change / tab close — this is
      // why the landing page_view / funnel events were being lost mid-navigation.
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event,
        properties,
        platform: 'web',
        app_version: '1.0.0',
        // Canonical contract: per-event UUID (server dedups on it) + stable
        // device id (anonymous-visitor counting + pre-login attribution).
        event_id: _uuid(),
        device_id: getDeviceId(),
        // session_id enables funnel correlation across events in one browsing
        // session. Backend already reads it (analytics.py track_event).
        session_id: getSessionId(),
      }),
    }).catch(() => {});
  } catch {}
}
